import { state } from '../state';
import type { VehicleData } from '../api/types';
import { updateTopbarStats } from './topbar';
import { formatTimeDiff } from '../utils/time';
import { initVehicleDetail, syncSelection, updateFleetSummary, selectVehicle } from './vehicle-detail';
import { escapeHtml } from '../utils/html';
import { formatSpeed } from '../utils/units';

let interactionsReady = false;
let detailReady       = false;
let lastDetailVersion = -1;

export function displayVehicles(vehicles: VehicleData[], isUserAction = false): void {
  const grid = document.getElementById('vehicleGrid') as HTMLElement;

  // Apply filters
  let filtered = vehicles.filter((v) => {
    if (state.filters.search && !v.device.name.toLowerCase().includes(state.filters.search.toLowerCase())) {
      return false;
    }
    if (state.filters.status.length > 0 && !state.filters.status.includes(v.healthStatus)) {
      return false;
    }
    if (state.filters.engine.length > 0) {
      if (state.filters.engine.includes('running') && !v.engineRunning) return false;
      if (state.filters.engine.includes('off') && v.engineRunning) return false;
    }
    return true;
  });

  // Sort: critical first, then warning, then healthy; within tier sort by fault count desc
  const statusOrder: Record<string, number> = { critical: 0, warning: 1, healthy: 2 };
  filtered.sort((a, b) => {
    const sd = statusOrder[a.healthStatus] - statusOrder[b.healthStatus];
    if (sd !== 0) return sd;
    return b.vehicleFaultCount - a.vehicleFaultCount;
  });

  state.currentFilteredVehicles = filtered;

  updateTopbarStats(vehicles);

  // Wire up delegated grid interactions once
  if (!interactionsReady) {
    // Delegated click/keyboard selection — avoids per-bubble closure churn on refresh.
    // Clicking a card opens the centered detail modal.
    grid.addEventListener('click', (e) => {
      const b = (e.target as Element).closest<HTMLElement>('.vbubble[data-id]');
      if (b) selectVehicle(b.dataset.id!);
    });
    grid.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const b = (e.target as Element).closest<HTMLElement>('.vbubble[data-id]');
      if (b) { e.preventDefault(); selectVehicle(b.dataset.id!); }
    });

    interactionsReady = true;
  }

  const dataVersion = state.dataVersion;

  // Init detail panel once; update fleet summary only on fresh data, not on
  // every filter/search interaction (critical for large fleets).
  if (!detailReady) {
    initVehicleDetail();
    detailReady = true;
  }
  if (dataVersion !== lastDetailVersion) {
    updateFleetSummary();
    lastDetailVersion = dataVersion;
  }

  // Update "Live Fleet · N shown" label
  const liveLabel = document.getElementById('liveFleetLabel');
  if (liveLabel) liveLabel.textContent = `Live Fleet · ${filtered.length} shown`;

  // Snapshot existing bubbles
  const existingBubbles = new Map<string, HTMLElement>();
  grid.querySelectorAll<HTMLElement>('.vbubble[data-id]').forEach((el) => {
    existingBubbles.set(el.dataset.id!, el);
  });

  // ── Batch all DOM writes while the grid is detached from layout. ──
  // Each grid.appendChild / .remove on an attached element triggers a reflow;
  // for thousands of cards that is thousands of reflows per filter click and
  // freezes the page. Hiding the grid collapses them into a single reflow.
  const filteredIds = new Set(filtered.map((v) => v.device.id));
  const prevDisplay = grid.style.display;
  grid.style.display = 'none';

  // Remove bubbles that are no longer in the filtered set
  existingBubbles.forEach((el, id) => {
    if (!filteredIds.has(id)) el.remove();
  });

  // Update/create bubbles in sorted order.
  // grid.appendChild on an existing element moves it to the end without removing it from the
  // document, so CSS animations do not restart on existing bubbles.
  // `prevSibling` lets us skip the appendChild call entirely when a bubble is already in the
  // right spot — on a stable refresh (no reorder) this turns ~3000 DOM moves into ~0 for
  // large fleets, which matters every 10s on a silent background refresh.
  let prevSibling: HTMLElement | null = null;
  filtered.forEach((vehicle) => {
    const id    = vehicle.device.id;
    const isSel = id === state.selectedVehicleId;
    let bubble  = existingBubbles.get(id);

    const sig = bubbleSig(vehicle);

    if (bubble) {
      // Compute class including selection — prevents the highlight being wiped on filter changes
      const newClass = `vbubble ${vehicle.healthStatus}${isSel ? ' hero-selected' : ''}`;
      if (bubble.className !== newClass) bubble.className = newClass;
      bubble.setAttribute('aria-selected', String(isSel));

      // Rebuild innerHTML only when the card's *visible* content changed. On a stable fleet a
      // background refresh now rewrites ~0 cards instead of all N (the old check rebuilt every
      // card every refresh because dataVersion always changes).
      if (bubble.dataset.sig !== sig) {
        bubble.innerHTML = buildBubbleHTML(vehicle);
        bubble.dataset.sig = sig;
      }
    } else {
      bubble = document.createElement('div');
      bubble.className = `vbubble ${vehicle.healthStatus}${isSel ? ' hero-selected' : ''}`;
      bubble.dataset.id = id;
      bubble.dataset.sig = sig;
      bubble.setAttribute('role', 'option');
      bubble.setAttribute('aria-selected', String(isSel));
      bubble.tabIndex = 0;
      bubble.innerHTML = buildBubbleHTML(vehicle);
    }

    // Insert/move only if not already immediately after the previous bubble in sorted order
    const alreadyInPlace = prevSibling
      ? prevSibling.nextElementSibling === bubble
      : grid.firstElementChild === bubble;
    if (!alreadyInPlace) grid.appendChild(bubble);
    prevSibling = bubble;
  });

  // Re-attach to layout — single reflow for the whole batch
  grid.style.display = prevDisplay;

  // Sync selection state (auto-select on first load, update panel on refresh)
  syncSelection(vehicles);
}

// Signature of everything a card actually shows. Used to skip the innerHTML rebuild for cards
// whose visible content is unchanged. Includes the formatted "last seen" so the relative time
// still ticks over when its bucket changes.
function bubbleSig(v: VehicleData): string {
  const lastSeen = v.lastCommunication ? formatTimeDiff(v.lastCommunication) : '—';
  return `${v.device.name}|${v.healthStatus}|${v.engineRunning ? 1 : 0}|${v.speed}|${v.vehicleFaultCount}|${lastSeen}|${state.odometerUnit}`;
}

function buildBubbleHTML(vehicle: VehicleData): string {
  const statusIcon = vehicle.healthStatus === 'critical' ? '◈' : vehicle.healthStatus === 'warning' ? '◇' : '◆';
  // Badge uses vehicleFaultCount (ECU faults only) so health label and count match
  const faultBadge =
    vehicle.vehicleFaultCount > 0
      ? `<span class="vb-badge">${statusIcon} ${vehicle.healthStatus.toUpperCase()} · ${vehicle.vehicleFaultCount}</span>`
      : `<span class="vb-badge">◆ HEALTHY</span>`;

  const lastSeen = vehicle.lastCommunication ? formatTimeDiff(vehicle.lastCommunication) : '—';

  return `
    <div class="vb-head">
      <div class="vb-name">${escapeHtml(vehicle.device.name)}</div>
      <div class="vb-dot"></div>
    </div>
    <div class="vb-summary">
      <span>${vehicle.engineRunning ? 'Running' : 'Off'}</span>
      <span style="opacity:0.3">·</span>
      <span class="vb-sumval">${formatSpeed(vehicle.speed, state.odometerUnit)}</span>
      ${vehicle.vehicleFaultCount > 0
        ? `<span style="opacity:0.3">·</span><span style="color:var(--sc);font-weight:600;">${vehicle.vehicleFaultCount} fault${vehicle.vehicleFaultCount > 1 ? 's' : ''}</span>`
        : ''}
    </div>
    ${faultBadge}
    <span class="vb-seen mono">${lastSeen}</span>`;
}
